import { activePortalRegion } from '../config/portal-regions';
import RegionSelector from '../components/RegionSelector';
import React, { useState, useEffect, useRef, FormEvent, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, Lock, Building, Mail, User, KeyRound, Fingerprint, Shield, Check, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useAuth } from '../contexts/AuthContext';
import { Esc, Enter } from '../components/KeyboardShortcuts';
import toast from 'react-hot-toast';
import MaintenanceBanner from '../components/MaintenanceBanner';
import {
  authViewToPath,
  buildAuthSearch,
  isAuthEntryPath,
  normalizePathname,
  pathToAuthView,
  type AuthView,
} from '../utils/authPaths';
import PasswordCriteriaList from '../components/PasswordCriteriaList';
import {
  confirmEmailForgotPassword,
  confirmOwnerForgotPassword,
  ForgotPasswordApiError,
  startForgotPassword,
} from '../utils/authForgotPassword';
import { isPasswordValid } from '../utils/passwordCriteria';
import { fetchAuthSignInHint, type SignInRoute } from '../utils/authSignInHint';

import { clearSignupLegalConfirmation, rememberSignupLegalConfirmation } from '../services/signupLegalAcceptance';

import { getSignupLegalRelease, type LegalRelease } from '../services/legalDocuments';

type SsoType = 'azure' | 'google' | 'saml';
type ProviderLinkDetails = {
  providerName: string;
  providerEmail: string;
  accountEmail: string;
  verificationEmail: string;
  matchesRequestedAccount: boolean;
};

function formatFederatedCallbackError(search: string): string | null {
  const params = new URLSearchParams(search);
  const rawDescription = params.get('error_description');
  if (!rawDescription) return null;
  if (rawDescription.includes('LINK_VERIFICATION_REQUIRED:')) return null;

  const description = rawDescription
    .replace(/^PreSignUp failed with error\s*/i, '')
    .replace(/^Sign up failed\.\s*/i, '')
    .trim();

  return description ? `Sign-in failed. ${description}` : 'Sign-in failed. Please try again.';
}

function providerLinkChallenge(search: string): string | null {
  const description = new URLSearchParams(search).get('error_description') || '';
  return /LINK_VERIFICATION_REQUIRED:([a-f0-9]{64}\.[A-Za-z0-9_-]{32})/.exec(description)?.[1] || null;
}

function resolveRouteType(route: SignInRoute): SsoType {
  const hintedType = route.t;
  if (hintedType === 'azure' || hintedType === 'google' || hintedType === 'saml') {
    return hintedType;
  }
  if (/^org[_-]/i.test(route.p)) {
    return 'saml';
  }
  return 'azure';
}

type AupChecks = { noColdemail: boolean; ownedBrands: boolean; transactional: boolean };

function AupAcknowledgementModal({
  title,
  confirmLabel,
  checks,
  allChecked,
  loading,
  onChecksChange,
  onCancel,
  onConfirm,
  forSignup = false,
  release,
}: {
  title: string;
  confirmLabel: string;
  checks: AupChecks;
  allChecked: boolean;
  loading: boolean;
  onChecksChange: React.Dispatch<React.SetStateAction<AupChecks>>;
  onCancel: () => void;
  onConfirm: () => void;
  forSignup?: boolean;
  release?: LegalRelease | null;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 dark:bg-black/70">
      <div className="w-full max-w-md space-y-5 rounded-xl border border-zinc-200 bg-white p-5 shadow-xl dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
        <div className="space-y-1">
          <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100"><span>{title}</span></h3>
          <p className="text-xs leading-relaxed text-zinc-700 dark:text-zinc-300">
            Simply Send is a permission-based email platform. Please confirm you understand and agree to the following before proceeding.
          </p>
        </div>

        <div className="space-y-3">
          <label className="relative flex items-start gap-3 cursor-pointer group">
            <input id="aup-no-cold-email" type="checkbox" checked={checks.noColdemail} onChange={(e) => onChecksChange(p => ({ ...p, noColdemail: e.target.checked }))} className="peer sr-only" />
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-zinc-300 bg-white transition-colors peer-checked:border-cyan-700 peer-checked:bg-cyan-700 dark:peer-checked:border-cyan-700 dark:peer-checked:bg-cyan-700 peer-focus-visible:ring-2 peer-focus-visible:ring-cyan-500 dark:border-zinc-600 dark:bg-zinc-800" />
            <Check className="pointer-events-none absolute left-0.5 top-1 h-3 w-3 text-white opacity-0 peer-checked:opacity-100" strokeWidth={3} />
            <span className="text-xs leading-relaxed text-zinc-600 transition-colors group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-zinc-100">{release?.prompts.acknowledgements.noColdemail}</span>
          </label>
          <label className="relative flex items-start gap-3 cursor-pointer group">
            <input id="aup-owned-brands" type="checkbox" checked={checks.ownedBrands} onChange={(e) => onChecksChange(p => ({ ...p, ownedBrands: e.target.checked }))} className="peer sr-only" />
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-zinc-300 bg-white transition-colors peer-checked:border-cyan-700 peer-checked:bg-cyan-700 dark:peer-checked:border-cyan-700 dark:peer-checked:bg-cyan-700 peer-focus-visible:ring-2 peer-focus-visible:ring-cyan-500 dark:border-zinc-600 dark:bg-zinc-800" />
            <Check className="pointer-events-none absolute left-0.5 top-1 h-3 w-3 text-white opacity-0 peer-checked:opacity-100" strokeWidth={3} />
            <span className="text-xs leading-relaxed text-zinc-600 transition-colors group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-zinc-100">{release?.prompts.acknowledgements.ownedBrands}</span>
          </label>
          <label className="relative flex items-start gap-3 cursor-pointer group">
            <input id="aup-transactional" type="checkbox" checked={checks.transactional} onChange={(e) => onChecksChange(p => ({ ...p, transactional: e.target.checked }))} className="peer sr-only" />
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-zinc-300 bg-white transition-colors peer-checked:border-cyan-700 peer-checked:bg-cyan-700 dark:peer-checked:border-cyan-700 dark:peer-checked:bg-cyan-700 peer-focus-visible:ring-2 peer-focus-visible:ring-cyan-500 dark:border-zinc-600 dark:bg-zinc-800" />
            <Check className="pointer-events-none absolute left-0.5 top-1 h-3 w-3 text-white opacity-0 peer-checked:opacity-100" strokeWidth={3} />
            <span className="text-xs leading-relaxed text-zinc-600 transition-colors group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-zinc-100">{release?.prompts.acknowledgements.transactional}</span>
          </label>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800/60">
          <p className="text-xs leading-relaxed text-zinc-700 dark:text-zinc-300"><span>By selecting “{confirmLabel},” you agree to our <a href={release?.documents.find(doc => doc.id === 'terms')?.url || "https://simplysend.email/legal/terms"} target="_blank" rel="noopener noreferrer" className="text-zinc-900 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-100 dark:hover:text-cyan-300">Terms of Service</a>, <a href={release?.documents.find(doc => doc.id === 'dpa')?.url || "https://simplysend.email/legal/dpa"} target="_blank" rel="noopener noreferrer" className="text-zinc-900 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-100 dark:hover:text-cyan-300">Data Processing Addendum</a> and <a href={release?.documents.find(doc => doc.id === 'acceptable-use')?.url || "https://simplysend.email/legal/acceptable-use"} target="_blank" rel="noopener noreferrer" className="text-zinc-900 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-100 dark:hover:text-cyan-300">Acceptable Use Policy</a>, and acknowledge our <a href={release?.documents.find(doc => doc.id === 'privacy')?.url || "https://simplysend.email/legal/privacy"} target="_blank" rel="noopener noreferrer" className="text-zinc-900 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-100 dark:hover:text-cyan-300">Privacy Policy</a>.</span></p>
          <p className="mt-2 text-xs leading-relaxed text-zinc-700 dark:text-zinc-300"><span>{forSignup ? release?.prompts.statement : 'A root workspace owner records acceptance on behalf of the customer after sign-in.'}</span></p>
        </div>

        <div className="flex gap-3">
          <button id="aup-cancel" type="button" onClick={onCancel} className="inline-flex h-10 flex-1 items-center justify-center gap-1 rounded-md border border-zinc-300 px-4 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"><span>Cancel</span> <Esc className="ml-1" /></button>
          <button id="aup-confirm" type="button" disabled={!allChecked || loading} onClick={onConfirm} className="inline-flex h-10 flex-1 items-center justify-center gap-1 rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-40">
            {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 mx-auto"></div> : <><span>{confirmLabel}</span><Enter className="ml-1" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { resolvedTheme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const authView = pathToAuthView(location.pathname);
  const themeToggle = (
    <button
      type="button"
      onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
      aria-label="Switch color theme"
      title="Switch color theme"
      className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-600 shadow-sm transition-colors hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 sm:right-6 sm:top-6"
    >
      <Sun className="hidden h-4 w-4 dark:block" aria-hidden="true" />
      <Moon className="h-4 w-4 dark:hidden" aria-hidden="true" />
      <span className="sr-only">Switch color theme</span>
    </button>
  );

  const goToAuthView = useCallback((view: AuthView, options?: { replace?: boolean }) => {
    const target = authViewToPath(view);
    const search = buildAuthSearch(location.pathname, location.search);
    if (normalizePathname(location.pathname) === target && (location.search || '') === search) {
      return;
    }
    navigate(`${target}${search}`, { replace: options?.replace ?? true });
  }, [location.pathname, location.search, navigate]);

  // 1) Deep links (e.g. /azure/landing?token=…) → /sign-up?redirect=<path+query>
  // 2) Auth aliases (/login, /auth/signup, /) → canonical path, preserving redirect/ref
  useEffect(() => {
    const search = buildAuthSearch(location.pathname, location.search);

    if (!isAuthEntryPath(location.pathname)) {
      const target = authViewToPath(pathToAuthView(location.pathname));
      navigate(`${target}${search}`, { replace: true });
      return;
    }

    const canonical = authViewToPath(pathToAuthView(location.pathname));
    if (
      normalizePathname(location.pathname) !== canonical ||
      (location.search || '') !== search
    ) {
      navigate(`${canonical}${search}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const [loginStep, setLoginStep] = useState<'email' | 'sso-detected' | 'passkey' | 'password'>('email');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetRecovery, setResetRecovery] = useState<'email' | 'owner' | 'totp' | 'passkey' | null>(null);
  const [recoveryFlowId, setRecoveryFlowId] = useState('');
  const [resetStep, setResetStep] = useState<'codes' | 'new'>('codes');
  const [totpResetCode, setTotpResetCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [changePassword, setChangePassword] = useState('');
  const [changePasswordConfirm, setChangePasswordConfirm] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangePasswordConfirm, setShowChangePasswordConfirm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formNotice, setFormNotice] = useState<string | null>(null);
  const [connectingProvider, setConnectingProvider] = useState<string | null>(() => sessionStorage.getItem('simplysend:connectProvider'));
  const [providerLinkCode, setProviderLinkCode] = useState('');
  const [providerLinkBusy, setProviderLinkBusy] = useState(false);
  const [providerLinkSent, setProviderLinkSent] = useState(false);
  const [providerLinkMessage, setProviderLinkMessage] = useState<string | null>(null);
  const [providerLinkDetails, setProviderLinkDetails] = useState<ProviderLinkDetails | null>(null);
  const [providerLinkDetailsError, setProviderLinkDetailsError] = useState<string | null>(null);
  // AUP acknowledgement modal state (in-flow overlay on sign-up — not a separate route)
  const [signupRelease, setSignupRelease] = useState<LegalRelease | null>(null);
  const [showAupModal, setShowAupModal] = useState(false);
  const [aupChecks, setAupChecks] = useState({ noColdemail: false, ownedBrands: false, transactional: false });
  const allAupChecked = aupChecks.noColdemail && aupChecks.ownedBrands && aupChecks.transactional;
  const pendingFederatedLoginRef = useRef<{ action: () => Promise<void>; providerLabel: string } | null>(null);
  const [federatedLoginProvider, setFederatedLoginProvider] = useState<string | null>(null);
  const [signupLoading, setSignupLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const MAX_OTP_ATTEMPTS = 3;
  const { login, loginWithPasskey, loginWithSSO, loginWithMicrosoft, loginWithGoogle, loginWithSocialProvider, signup, confirmSignup, loading, error: authError, mfaRequired, newPasswordRequired, passwordResetRequired, passwordResetEmail, setPasswordResetRequired, resetMfa, clearError } = useAuth();
  const federatedCallbackError = formatFederatedCallbackError(location.search);
  const linkChallenge = providerLinkChallenge(location.search);
  const providerLinkProviderLabel = providerLinkDetails?.providerName === 'LoginWithAmazon'
    ? 'Amazon'
    : providerLinkDetails?.providerName && (providerLinkDetails.providerName.startsWith('MicrosoftEntra') || /^Az[0-9a-f]{30}$/i.test(providerLinkDetails.providerName))
      ? 'Microsoft'
      : providerLinkDetails?.providerName || 'Provider';
  useEffect(() => {
    if (!linkChallenge) return;
    const publicApiBase = (activePortalRegion.publicApiUrl || (activePortalRegion.id === 'us' ? import.meta.env.VITE_PUBLIC_API_URL : 'https://regional-endpoint-not-configured.invalid')) || '';
    if (!publicApiBase) return;
    let cancelled = false;
    void fetch(`${publicApiBase}/auth-provider-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'status', challengeId: linkChallenge, expectedAccountId: sessionStorage.getItem('simplysend:connectAccountId') || undefined }),
    }).then(async (response) => {
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not load the sign-in details. Please try again.');
      if (!cancelled) setProviderLinkDetails(result as ProviderLinkDetails);
    }).catch((error) => {
      if (!cancelled) setProviderLinkDetailsError(error.message || 'Could not load the sign-in details. Please try again.');
    });
    return () => { cancelled = true; };
  }, [linkChallenge]);
  const error = authView !== 'confirm-email' ? (authError || formError || federatedCallbackError) : formError;
  const [ssoCheckLoading, setSsoCheckLoading] = useState(false);
  const [ssoResult, setSsoResult] = useState<SignInRoute | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyRequired, setPasskeyRequired] = useState(false);
  const passkeysSupported =
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined';
  const ssoRouteType = ssoResult ? resolveRouteType(ssoResult) : null;
  const ssoRouteLabel = ssoResult ? (ssoResult.n || ssoResult.d || 'SSO') : '';

  const clearLoginErrors = useCallback(() => {
    setFormError(null);
    setFormNotice(null);
    clearError?.();
  }, [clearError]);

  const goBackToEmailStep = useCallback(() => {
    clearLoginErrors();
    setLoginStep('email');
    setSsoResult(null);
    setPasskeyRequired(false);
    setPassword('');
  }, [clearLoginErrors]);

  // Admin-forced password reset: keep Cognito challenge UX and sync the URL.
  useEffect(() => {
    if (passwordResetRequired && authView !== 'reset-password') {
      goToAuthView('reset-password');
    }
  }, [passwordResetRequired, authView, goToAuthView]);

  // Cold / refresh visits to mid-flow routes without session state → safe fallback.
  useEffect(() => {
    if (authView === 'confirm-email' && !pendingEmail && !email) {
      goToAuthView('sign-up');
    }
  }, [authView, pendingEmail, email, goToAuthView]);

  useEffect(() => {
    if (
      authView === 'reset-password' &&
      !passwordResetRequired &&
      !email &&
      !passwordResetEmail &&
      !forgotPasswordEmail
    ) {
      goToAuthView('forgot-password');
    }
  }, [authView, passwordResetRequired, email, passwordResetEmail, forgotPasswordEmail, goToAuthView]);

  // Focus email input when entering email step on sign-in
  useEffect(() => {
    if (authView === 'sign-in' && loginStep === 'email') {
      emailInputRef.current?.focus();
    }
  }, [loginStep, authView]);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearLoginErrors();

    // Email step: validate locally, then route SSO domains to their IdP.
    if (loginStep === 'email') {
      if (!email.trim() || !email.includes('@')) {
        setFormError('Please enter a valid email address.');
        return;
      }

      setLoginEmail(email);
      setPasskeyRequired(false);
      setSsoResult(null);
      setSsoCheckLoading(true);
      try {
        const hint = await fetchAuthSignInHint(email);
        if (hint.r?.p) {
          setSsoResult(hint.r);
          setLoginStep('sso-detected');
          return;
        }
      } catch {
        // Lookup failure must not block password/passkey sign-in.
      } finally {
        setSsoCheckLoading(false);
      }
      setLoginStep('password');
      return;
    }

    if (loginStep !== 'password') {
      return;
    }

    // Step 2: Native Password Login
    try {
      setLoginEmail(email);
      setLoginPassword(password);
      const result = await login(email, password);

      console.log('[Login] login result:', JSON.stringify(result));

      if (result?.requiresMfa) {
        // The login function will set mfaRequired to true if MFA is needed
        return;
      } else if (result?.requiresEmailConfirmation) {
        toast.error('Email not verified. Contact support to reset your account.');
        return;
      } else if (result?.requiresPasswordReset) {
        // Admin-initiated password reset: show the code + new password form
        console.log('[Login] Navigating to reset-password');
        setEmail(email);
        goToAuthView('reset-password');
        setFormError('Your password has been reset by an administrator. A verification code has been sent to your email.');
        return;
      }
    } catch (loginErr) {
      // Error is already handled in AuthContext and set in error state
      console.error('[Login] handleLogin catch:', loginErr);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!mfaCode || mfaCode.length !== 6) {
      setFormError('Please enter a valid 6-digit code');
      return;
    }

    try {
      await login(loginEmail, loginPassword, mfaCode);
      setMfaCode('');
    } catch {
      // Error is already handled in AuthContext
      setMfaCode('');
    }
  };

  const handleBackToLogin = () => {
    resetMfa();
    setMfaCode('');
    setFormError(null);
  };

  const handlePasskeySignIn = async () => {
    setFormError(null);
    if (!email.trim() || !email.includes('@')) {
      setFormError('Enter your work email first, then use passkey sign-in.');
      return;
    }
    if (!passkeysSupported) {
      if (!passkeyRequired) {
        setFormError('Passkeys are not supported in this browser. Sign in with your password instead.');
      } else {
        setFormError('Passkeys are not supported in this browser. Use a passkey-capable browser or contact support.');
      }
      return;
    }

    try {
      setPasskeyLoading(true);
      setLoginEmail(email);
      const result = await loginWithPasskey(email);
      if (result?.requiresEmailConfirmation) {
        toast.error('Email not verified. Contact support to reset your account.');
      } else if (!result?.success && !result?.requiresMfa && !passkeyRequired && result?.cancelled) {
        setLoginStep('password');
      }
    } catch (passkeyErr) {
      console.error('[Login] passkey sign-in failed:', passkeyErr);
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (changePassword !== changePasswordConfirm) {
      setFormError('Passwords do not match');
      return;
    }

    if (!isPasswordValid(changePassword)) {
      setFormError('Password does not meet the required criteria');
      return;
    }

    try {
      await login(loginEmail, loginPassword, undefined, changePassword);
      setChangePassword('');
      setChangePasswordConfirm('');
    } catch {
      // Error is already handled in AuthContext
    }
  };

  const handleSignupClick = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    const trimmedName = name.trim();
    const nameParts = trimmedName.split(/\s+/);
    if (nameParts.length < 2 || nameParts.some(p => p.length === 0)) {
      setFormError("Please enter your full name (first middle last).");
      return;
    }

    if (!isPasswordValid(password)) {
      setFormError('Password does not meet the required criteria');
      return;
    }

    setSignupLoading(true);
    try { setSignupRelease(await getSignupLegalRelease()); }
    catch (reason) { setFormError(reason instanceof Error ? reason.message : 'Legal agreements could not be loaded.'); return; }
    finally { setSignupLoading(false); }
    // Show AUP acknowledgement modal before proceeding
    setAupChecks({ noColdemail: false, ownedBrands: false, transactional: false });
    setShowAupModal(true);
  };

  const handleSignup = async () => {
    if (!allAupChecked || !signupRelease) return;
    setFormError(null);
    setShowAupModal(false);
    setSignupLoading(true);

    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref') || "";

    try {
      await rememberSignupLegalConfirmation(signupRelease!, email);
      const success = await signup(email, password, name, referralCode);
      if (success) {
        // Set before navigation so /confirm-email render has an address even if
        // the post-await state update and router update land in separate commits.
        setPendingEmail(email);
        setOtpAttempts(0);
        setFormError(null);
        goToAuthView('confirm-email');
      } else {
        clearSignupLegalConfirmation();
        setPendingEmail('');
      }
    } catch (err: unknown) {
      clearSignupLegalConfirmation();
      const errorMessage = err instanceof Error ? err.message : 'Signup failed';
      setFormError(errorMessage);
      setPendingEmail('');
    } finally {
      setSignupLoading(false);
    }
  };

  const dismissAupModal = () => {
    pendingFederatedLoginRef.current = null;
    setFederatedLoginProvider(null);
    setShowAupModal(false);
    setAupChecks({ noColdemail: false, ownedBrands: false, transactional: false });
  };

  const requestFederatedLogin = async (loginAction: () => Promise<void>, providerLabel: string) => {
    clearLoginErrors();
    // Existing owners review missing legal acceptance after authentication.
    // A provider sign-in must not ask every member to accept for the company.
    if (authView !== 'sign-up') {
      setFederatedLoginProvider(providerLabel);
      try {
        sessionStorage.removeItem('simplysend:connectProvider');
        sessionStorage.setItem('simplysend:pendingProviderLinkProvider', providerLabel);
        await loginAction();
      } finally { setFederatedLoginProvider(null); }
      return;
    }
    try { setSignupRelease(await getSignupLegalRelease()); }
    catch (reason) { setFormError(reason instanceof Error ? reason.message : 'Legal agreements could not be loaded.'); return; }
    pendingFederatedLoginRef.current = { action: loginAction, providerLabel };
    setAupChecks({ noColdemail: false, ownedBrands: false, transactional: false });
    setShowAupModal(true);
  };

  const handleAupConfirmation = async () => {
    if (!signupRelease || !allAupChecked) return;
    const pendingFederatedLogin = pendingFederatedLoginRef.current;
    if (pendingFederatedLogin) {
      pendingFederatedLoginRef.current = null;
      setShowAupModal(false);
      setFederatedLoginProvider(pendingFederatedLogin.providerLabel);
      try {
        sessionStorage.removeItem('simplysend:connectProvider');
        sessionStorage.setItem('simplysend:pendingProviderLinkProvider', pendingFederatedLogin.providerLabel);
        if (authView === 'sign-up') await rememberSignupLegalConfirmation(signupRelease!, '');
        await pendingFederatedLogin.action();
      } finally {
        setFederatedLoginProvider(null);
      }
      return;
    }

    await handleSignup();
  };

  const handleAupConfirmationRef = useRef(handleAupConfirmation);

  useEffect(() => {
    handleAupConfirmationRef.current = handleAupConfirmation;
  });

  useEffect(() => {
    if (!showAupModal) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismissAupModal();
      } else if (event.key === 'Enter' && allAupChecked && !loading) {
        const target = event.target as HTMLElement;
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
        if (isInput) return;
        event.preventDefault();
        handleAupConfirmationRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [showAupModal, allAupChecked, loading]);

  const handleConfirmation = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (otpAttempts >= MAX_OTP_ATTEMPTS) {
      return;
    }

    const emailToConfirm = pendingEmail || email;
    let succeeded = false;
    setConfirmLoading(true);

    try {
      const result = await confirmSignup(emailToConfirm, confirmationCode);
      succeeded = !!result.userId;
    } catch {
      succeeded = false;
    }

    if (!succeeded) {
      setConfirmLoading(false);
      // Confirmation failed — keep user on OTP screen
      const newAttempts = otpAttempts + 1;
      setOtpAttempts(newAttempts);
      setConfirmationCode('');
      if (newAttempts >= MAX_OTP_ATTEMPTS) {
        setFormError('Too many incorrect attempts. Redirecting to login...');
        setTimeout(() => resetToLogin(), 2000);
      } else {
        setFormError(`Invalid verification code. ${MAX_OTP_ATTEMPTS - newAttempts} attempt${MAX_OTP_ATTEMPTS - newAttempts === 1 ? '' : 's'} remaining.`);
      }
      return;
    }

    // The confirmation code is now consumed. Do not leave the user on this
    // screen while an automatic sign-in or profile request is still pending.
    // They can sign in normally from a known, terminal state instead.
    setPendingEmail('');
    setConfirmationCode('');
    setOtpAttempts(0);
    setConfirmLoading(false);
    clearError?.();
    setPassword('');
    setLoginStep('password');
    setFormNotice('Email confirmed. Sign in with your new password to continue.');
    goToAuthView('sign-in');
  };

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    const resetEmail = forgotPasswordEmail.trim().toLowerCase();
    if (!resetEmail.includes('@')) {
      setFormError('Please enter a valid email address.');
      return;
    }

    try {
      setForgotLoading(true);
      const flowId = await startForgotPassword(resetEmail);
      setResetRecovery('email');
      setRecoveryFlowId(flowId);
      setEmail(resetEmail);
      setForgotPasswordEmail(resetEmail);
      setResetStep('codes');
      setTotpResetCode('');
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');

      goToAuthView('reset-password');
    } catch (error) {
      if (error instanceof Error) {
        setFormError(error.message || 'Failed to send reset code');
      } else {
        setFormError('An unknown error occurred while processing your request');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const handleUseOwnerResetCode = () => {
    const resetEmail = forgotPasswordEmail.trim().toLowerCase();
    if (!resetEmail.includes('@')) {
      setFormError('Enter the email address that received the owner reset code.');
      return;
    }

    setFormError(null);
    setEmail(resetEmail);
    setResetRecovery('owner');
    setRecoveryFlowId('');
    setResetStep('codes');
    setResetCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    goToAuthView('reset-password');
  };

  const returnToForgotStart = (message: string) => {
    setResetStep('codes');
    setResetCode('');
    setTotpResetCode('');
    setResetRecovery(null);
    setRecoveryFlowId('');
    setNewPassword('');
    setConfirmNewPassword('');
    setFormError(message);
    goToAuthView('forgot-password');
  };

  const handleResetPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (newPassword !== confirmNewPassword) {
      return; // Error will be shown in UI
    }

    if (!isPasswordValid(newPassword)) {
      setFormError('Password does not meet the required criteria');
      return;
    }

    // Use local email or the context-preserved email from admin reset flow
    const resetEmail = email || passwordResetEmail || forgotPasswordEmail;

    try {
      setForgotLoading(true);
      try {
        if (!passwordResetRequired && resetRecovery === 'email') {
          await confirmEmailForgotPassword(resetEmail, recoveryFlowId, resetCode, newPassword);
        } else {
          await confirmOwnerForgotPassword(resetEmail, resetCode, newPassword);
        }
      } catch (error) {
        if (!passwordResetRequired && resetRecovery === 'email') {
          const message = error instanceof Error ? error.message : 'Failed to reset password';
          returnToForgotStart(message);
          return;
        }
        if (error instanceof ForgotPasswordApiError && error.restart) {
          returnToForgotStart(error.message || 'Verification expired. Request a new reset code.');
          return;
        }
        if (error instanceof Error) {
          setFormError(error.message || 'Failed to reset password');
        } else {
          setFormError('An unknown error occurred while resetting your password');
        }
        return;
      }

      setPasswordResetRequired(false);
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTotpResetCode('');
      setResetRecovery(null);
      setRecoveryFlowId('');
      setResetStep('codes');
      setEmail(resetEmail);
      setForgotPasswordEmail('');
      setPassword('');
      setLoginStep('email');
      setFormError(null);
      toast.success('Password updated. Sign in with your new password.');
      goToAuthView('sign-in');
    } catch (error) {
      if (error instanceof Error) {
        setFormError(error.message || 'Failed to reset password');
      } else {
        setFormError('An unknown error occurred while resetting your password');
      }
    } finally {
      setForgotLoading(false);
    }
  };
  const resetToLogin = () => {
    clearLoginErrors();
    setPasswordResetRequired(false);
    setPendingEmail('');
    setConfirmationCode('');
    setResetCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setForgotPasswordEmail('');
    setTotpResetCode('');
    setResetRecovery(null);
    setResetStep('codes');
    setEmail('');
    setPassword('');
    setName('');
    setLoginStep('email');
    setSsoResult(null);
    goToAuthView('sign-in');
  };

  // Show confirmation screen if we have a pending email
  const confirmEmailAddress = pendingEmail || email;
  if (authView === 'confirm-email' && confirmEmailAddress) {
    return (
      <div className="relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 flex items-center justify-center px-4 py-8 sm:py-12">
        {themeToggle}
        <div className="w-full max-w-md space-y-6 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="mb-1 flex justify-center">
                <a href="https://simplysend.email" aria-label="Simply Send home">
                  <span className="relative block h-9 w-9"><img src="/assets/images/sse-logo-bw-256x.png" alt="" className="h-full w-full object-contain dark:hidden" /><img src="/assets/images/sse-logo-bw-256x-dark.png" alt="" className="hidden h-full w-full object-contain dark:block" /></span>
                </a>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              Confirm Your Email
            </h2>
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
              We sent a confirmation code to <strong><span>{confirmEmailAddress}</span></strong>
            </p>
            <div className="mt-4 rounded-md border border-cyan-200 bg-cyan-50 p-3 text-left dark:border-cyan-800 dark:bg-cyan-950/50">
              <p className="text-xs font-medium leading-relaxed text-cyan-900 dark:text-cyan-100">
                <span className="mr-1">📧</span> Check your email inbox (including spam or junk folders) for a 6-digit code with the subject line <strong>"Verify your new account"</strong>.
              </p>
            </div>
          </div>

          <form className="mt-6 space-y-4" onSubmit={handleConfirmation}>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/40">
                <p className="text-sm text-red-700 dark:text-red-200"><span>{error}</span></p>
              </div>
            )}

            <div>
              <label htmlFor="confirmationCode" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Confirmation Code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <KeyRound className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                </div>
                <input
                  id="confirmationCode"
                  name="confirmationCode"
                  type="text"
                  required
                  maxLength={6}
                  className="h-10 w-full pl-10 pr-4 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm text-center tracking-widest"
                  placeholder="000000"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                Enter the 6-digit code from your email
              </p>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={confirmLoading || confirmationCode.length !== 6}
                className="inline-flex h-10 w-full items-center justify-center rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {confirmLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                    <span>Verifying...</span>
                  </div>
                ) : (
                  <span>Confirm Email</span>
                )}
              </button>
            </div>

            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={resetToLogin}
                className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
              >
                Back to login
              </button>
              <p className="text-xs text-zinc-600 dark:text-zinc-400">
                Didn't receive the code? Check your spam folder or try registering again.
              </p>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Show forgot password screen
  if (authView === 'forgot-password') {
    return (
      <div className="relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 flex items-center justify-center px-4 py-8 sm:py-12">
        {themeToggle}
        <div className="w-full max-w-md space-y-6 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="mb-1 flex justify-center">
                <a href="https://simplysend.email" aria-label="Simply Send home">
                  <span className="relative block h-9 w-9"><img src="/assets/images/sse-logo-bw-256x.png" alt="" className="h-full w-full object-contain dark:hidden" /><img src="/assets/images/sse-logo-bw-256x-dark.png" alt="" className="hidden h-full w-full object-contain dark:block" /></span>
                </a>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              <span>Reset Your Password</span>
            </h2>
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
              <span>
                {resetRecovery === 'passkey'
                  ? 'Passkey accounts cannot reset a password from this page.'
                  : 'Enter your work email. If this address has a Simply Send password, we will send a reset code. If you sign in with your organization, use that on the sign-in page instead.'}
              </span>
            </p>
          </div>

          {resetRecovery === 'passkey' ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-center dark:border-amber-800 dark:bg-amber-950/40">
                <Fingerprint className="h-8 w-8 text-cyan-700 dark:text-cyan-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-900 dark:text-zinc-100">
                  <span>This account uses a passkey. Contact support to reset the password.</span>
                </p>
                <a
                  href="https://simplysend.email/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
                >
                  <span>Contact support</span>
                </a>
              </div>
              <div className="text-center">
                <button
                  type="button"
                  onClick={resetToLogin}
                  className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
                >
                  Back to login
                </button>
              </div>
            </div>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={handleForgotPassword}>
              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/40">
                  <p className="text-sm text-red-700 dark:text-red-200"><span>{error}</span></p>
                </div>
              )}

              <div>
                <label htmlFor="forgotEmail" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Work email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                  </div>
                  <input
                    id="forgotEmail"
                    name="forgotEmail"
                    type="email"
                    required
                    className="h-10 w-full pl-10 pr-4 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Enter your work email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={forgotLoading || !forgotPasswordEmail.trim()}
                  className="inline-flex h-10 w-full items-center justify-center rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {forgotLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2"></div>
                  ) : (
                    <span>Send Recovery Instructions</span>
                  )}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleUseOwnerResetCode}
                  disabled={forgotLoading || !forgotPasswordEmail.trim()}
                  className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors disabled:opacity-50"
                >
                  <span>I already have an owner reset code</span>
                </button>
                <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
                  <span>Use this only for a code sent after an authenticated owner or Admin reset. Earlier or self-service codes cannot unlock protected accounts.</span>
                </p>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={resetToLogin}
                  className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
                >
                  Back to login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Show reset password screen (from forgot password or admin-initiated reset)
  if (authView === 'reset-password' || passwordResetRequired) {
    const displayEmail = email || passwordResetEmail || forgotPasswordEmail;
    const totpResetFlow = false;
    const showNewPasswordFields = !totpResetFlow || resetStep === 'new';
    return (
      <div className="relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 flex items-center justify-center px-4 py-8 sm:py-12">
        {themeToggle}
        <div className="w-full max-w-md space-y-6 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="mb-1 flex justify-center">
                <a href="https://simplysend.email" aria-label="Simply Send home">
                  <span className="relative block h-9 w-9"><img src="/assets/images/sse-logo-bw-256x.png" alt="" className="h-full w-full object-contain dark:hidden" /><img src="/assets/images/sse-logo-bw-256x-dark.png" alt="" className="hidden h-full w-full object-contain dark:block" /></span>
                </a>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              <span>{totpResetFlow && resetStep === 'codes' ? 'Verify your identity' : 'Set New Password'}</span>
            </h2>
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
              <span>
                {totpResetFlow && resetStep === 'codes'
                  ? `Enter the email code sent to ${displayEmail} and your authenticator code.`
                  : totpResetFlow
                    ? 'Choose a new password for your account.'
                    : passwordResetRequired
                      ? `Follow the recovery instructions sent to ${displayEmail}. If the email includes a code, enter it below with your new password.`
                      : `If this address has a Simply Send password, we sent a reset code to ${displayEmail}. If you sign in with your organization, use that on the sign-in page. Simply Send cannot reset that password.`}
              </span>
            </p>
            {passwordResetRequired && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <span>Your password has been reset by an administrator. Please check your email for the verification code.</span>
                </p>
              </div>
            )}
          </div>

          <form className="mt-6 space-y-4" onSubmit={handleResetPassword}>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/40">
                <p className="text-sm text-red-700 dark:text-red-200"><span>{error}</span></p>
              </div>
            )}

            {(!totpResetFlow || resetStep === 'codes') && (
              <>
                <div>
                  <label htmlFor="resetCode" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                    <span>Email reset code</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <KeyRound className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                    </div>
                    <input
                      id="resetCode"
                      name="resetCode"
                      type="text"
                      required
                      maxLength={6}
                      className="h-10 w-full pl-10 pr-4 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm text-center tracking-widest"
                      placeholder="000000"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    />
                  </div>
                  <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                    <span>Enter the 6-digit code if one was included in your recovery email</span>
                  </p>
                </div>

                {totpResetFlow && (
                  <div>
                    <label htmlFor="totpResetCode" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      <span>Authenticator code</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Shield className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                      </div>
                      <input
                        id="totpResetCode"
                        name="totpResetCode"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        maxLength={6}
                        className="h-10 w-full pl-10 pr-4 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm text-center tracking-widest"
                        placeholder="000000"
                        value={totpResetCode}
                        onChange={(e) => setTotpResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      />
                    </div>
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                      <span>Enter the 6-digit code from your authenticator app</span>
                    </p>
                  </div>
                )}
              </>
            )}

            {showNewPasswordFields && (
              <>
                <div className="relative">
                  <label htmlFor="newPassword" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                    <span>New Password</span>
                  </label>
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none mt-6">
                    <Lock className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                  </div>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    className="h-10 w-full pl-10 pr-10 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Enter your new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center mt-6"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                    )}
                  </button>
                </div>

                <div className="relative">
                  <label htmlFor="confirmNewPassword" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                    <span>Confirm New Password</span>
                  </label>
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none mt-6">
                    <Lock className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                  </div>
                  <input
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    type={showConfirmNewPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    className="h-10 w-full pl-10 pr-10 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Confirm your new password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center mt-6"
                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  >
                    {showConfirmNewPassword ? (
                      <EyeOff className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                    )}
                  </button>
                </div>

                <PasswordCriteriaList
                  password={newPassword}
                  confirmPassword={confirmNewPassword}
                  showMatch
                  tone="adaptive"
                />
              </>
            )}

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={
                  forgotLoading || loading
                  || (totpResetFlow && resetStep === 'codes'
                    ? resetCode.length !== 6 || totpResetCode.length !== 6
                    : newPassword !== confirmNewPassword || !isPasswordValid(newPassword) || (!totpResetFlow && !resetCode))
                }
                className="inline-flex h-10 w-full items-center justify-center rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {forgotLoading || loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2"></div>
                ) : totpResetFlow && resetStep === 'codes' ? (
                  <span>Continue</span>
                ) : (
                  <span>Reset Password</span>
                )}
              </button>
            </div>

            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={() => {
                  setResetRecovery(null);
                  setResetStep('codes');
                  goToAuthView('forgot-password');
                }}
                className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
              >
                <span>Didn't receive code? Try again</span>
              </button>
              <br />
              <button
                type="button"
                onClick={resetToLogin}
                className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
              >
                Back to login
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Show signup form
  if (authView === 'sign-up') {
    return (
      <div className="relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 flex items-center justify-center px-4 py-8 sm:py-12">
        {themeToggle}
        <div className="w-full max-w-md space-y-6 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="mb-1 flex justify-center">
                <a href="https://simplysend.email" aria-label="Simply Send home">
                  <span className="relative block h-9 w-9"><img src="/assets/images/sse-logo-bw-256x.png" alt="" className="h-full w-full object-contain dark:hidden" /><img src="/assets/images/sse-logo-bw-256x-dark.png" alt="" className="hidden h-full w-full object-contain dark:block" /></span>
                </a>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              Create Your Account
            </h2>
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
              Get started with your <span translate="no">&nbsp;Simply Send&nbsp;</span> account
            </p>
          </div>

          <form className="mt-6 space-y-4" onSubmit={handleSignupClick}>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/40">
                <p className="text-sm text-red-700 dark:text-red-200"><span>{error}</span></p>
              </div>
            )}

            <div className="rounded-md shadow-sm space-y-4">
              <div>
                <label htmlFor="name" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Full Name (first middle last)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <User className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="h-10 w-full pl-10 pr-4 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Work email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="h-10 w-full pl-10 pr-4 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Enter your work email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="h-10 w-full pl-10 pr-10 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center rounded-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                    )}
                  </button>
                </div>
                <div className="mt-3">
                  <PasswordCriteriaList password={password} tone="adaptive" />
                </div>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed px-1 text-center">
              After clicking "Sign Up", you'll receive a verification email with a 6-digit code to confirm your account.
            </p>

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading || signupLoading || !isPasswordValid(password)}
                className="inline-flex h-10 w-full items-center justify-center rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {(loading || signupLoading) ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                    <span>Creating Account...</span>
                  </div>
                ) : (
                  <span>Sign Up</span>
                )}
              </button>
            </div>

            <p className="text-center text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed"><span>Regional accounts use the selected region for core account and email services. Submissions through the Simply Send SMTP bridge currently transit the US; website delivery and external providers have the locations described in our DPA and Privacy Policy.</span></p>

            <p className="text-center text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
              By signing up, you agree to our{' '}
              <a href="https://simplysend.email/legal/terms" target="_blank" rel="noopener noreferrer" className="text-zinc-800 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-200 dark:hover:text-cyan-300">Terms of Service</a>
              ,{' '}
              <a href="https://simplysend.email/legal/dpa" target="_blank" rel="noopener noreferrer" className="text-zinc-800 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-200 dark:hover:text-cyan-300">Data Processing Addendum</a>
              ,{' '}
              <a href="https://simplysend.email/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-zinc-800 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-200 dark:hover:text-cyan-300">Privacy Policy</a>
              , and{' '}
              <a href="https://simplysend.email/legal/acceptable-use" target="_blank" rel="noopener noreferrer" className="text-zinc-800 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-200 dark:hover:text-cyan-300">Acceptable Use Policy</a>.
            </p>

            <div className="text-center">
              <button
                type="button"
                onClick={() => goToAuthView('sign-in')}
                className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
              >
                Already have an account?
              </button>
            </div>
          </form>

          {/* AUP Acknowledgement Modal */}
          {showAupModal && (
            <AupAcknowledgementModal
              release={signupRelease}
              forSignup
              title="Before you create your account"
              confirmLabel="I Agree — Create Account"
              checks={aupChecks}
              allChecked={allAupChecked}
              loading={signupLoading}
              onChecksChange={setAupChecks}
              onCancel={dismissAupModal}
              onConfirm={() => { void handleAupConfirmation(); }}
            />
          )}
        </div>
      </div>
    );
  }

  // Show MFA verification form if required
  if (mfaRequired) {
    return (
      <div className="relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 flex items-center justify-center px-4 py-8 sm:py-12">
        {themeToggle}
        <div className="w-full max-w-md space-y-6 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="mb-1 flex justify-center">
                <a href="https://simplysend.email" aria-label="Simply Send home">
                  <span className="relative block h-9 w-9"><img src="/assets/images/sse-logo-bw-256x.png" alt="" className="h-full w-full object-contain dark:hidden" /><img src="/assets/images/sse-logo-bw-256x-dark.png" alt="" className="hidden h-full w-full object-contain dark:block" /></span>
                </a>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              Two-Factor Authentication
            </h2>
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <form className="mt-6 space-y-4" onSubmit={handleMfaSubmit}>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/40">
                <p className="text-sm text-red-700 dark:text-red-200"><span>{error}</span></p>
              </div>
            )}

            <div>
              <label htmlFor="mfaCode" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Verification Code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Shield className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
                </div>
                <input
                  id="mfaCode"
                  name="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  className="h-10 w-full pl-10 pr-4 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm text-center tracking-widest"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="inline-flex h-10 w-full items-center justify-center rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2"></div>
                ) : (
                  <span>Verify Code</span>
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
              >
                Back to login
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Show new password requirement form for migrated users
  if (newPasswordRequired) {
    return (
      <div className="relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 flex items-center justify-center px-4 py-8 sm:py-12">
        {themeToggle}
        <div className="w-full max-w-md space-y-6 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="mb-1 flex justify-center">
                <a href="https://simplysend.email" aria-label="Simply Send home">
                  <span className="relative block h-9 w-9"><img src="/assets/images/sse-logo-bw-256x.png" alt="" className="h-full w-full object-contain dark:hidden" /><img src="/assets/images/sse-logo-bw-256x-dark.png" alt="" className="hidden h-full w-full object-contain dark:block" /></span>
                </a>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              Set New Password
            </h2>
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
              Please set a new password for your account
            </p>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              Your account has been migrated. You need to set a new password to continue.
            </p>
          </div>

          <form className="mt-6 space-y-4" onSubmit={handleNewPasswordSubmit}>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/40">
                <p className="text-sm text-red-700 dark:text-red-200"><span>{error}</span></p>
              </div>
            )}

            <div className="relative">
              <label htmlFor="changePassword" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                New Password
              </label>
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none mt-6">
                <Lock className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
              </div>
              <input
                id="changePassword"
                name="changePassword"
                type={showChangePassword ? 'text' : 'password'}
                required
                className="h-10 w-full pl-10 pr-10 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                placeholder="Enter your new password"
                value={changePassword}
                onChange={(e) => setChangePassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center mt-6"
                onClick={() => setShowChangePassword(!showChangePassword)}
              >
                {showChangePassword ? (
                  <EyeOff className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                ) : (
                  <Eye className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                )}
              </button>
            </div>

            <div className="relative">
              <label htmlFor="changePasswordConfirm" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Confirm New Password
              </label>
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none mt-6">
                <Lock className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
              </div>
              <input
                id="changePasswordConfirm"
                name="changePasswordConfirm"
                type={showChangePasswordConfirm ? 'text' : 'password'}
                required
                className="h-10 w-full pl-10 pr-10 py-2 border border-zinc-300 bg-white text-zinc-900 placeholder-zinc-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-400 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                placeholder="Confirm your new password"
                value={changePasswordConfirm}
                onChange={(e) => setChangePasswordConfirm(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center mt-6"
                onClick={() => setShowChangePasswordConfirm(!showChangePasswordConfirm)}
              >
                {showChangePasswordConfirm ? (
                  <EyeOff className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                ) : (
                  <Eye className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
                )}
              </button>
            </div>

            <PasswordCriteriaList
              password={changePassword}
              confirmPassword={changePasswordConfirm}
              showMatch
              tone="adaptive"
            />

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading || changePassword !== changePasswordConfirm || !isPasswordValid(changePassword)}
                className="inline-flex h-10 w-full items-center justify-center rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2"></div>
                ) : (
                  <span>Set New Password</span>
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-sm text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200 underline underline-offset-2 transition-colors"
              >
                Back to login
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const submitProviderLink = async (action: 'request' | 'verify') => {
    if (!linkChallenge || !providerLinkDetails?.matchesRequestedAccount) return;
    const publicApiBase = (activePortalRegion.publicApiUrl || (activePortalRegion.id === 'us' ? import.meta.env.VITE_PUBLIC_API_URL : 'https://regional-endpoint-not-configured.invalid')) || '';
    if (!publicApiBase) {
      setProviderLinkMessage('Sign-in verification is unavailable. Please try again later.');
      return;
    }
    setProviderLinkBusy(true);
    setProviderLinkMessage(null);
    try {
      const response = await fetch(`${publicApiBase}/auth-provider-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, challengeId: linkChallenge, expectedAccountId: sessionStorage.getItem('simplysend:connectAccountId') || undefined, ...(action === 'verify' ? { code: providerLinkCode.trim() } : {}) }),
      });
      const result = await response.json();
      if (!response.ok) {
        setProviderLinkMessage(result.error || 'Verification failed. Please try again.');
        return;
      }
      if (action === 'request') {
        setProviderLinkSent(true);
        setProviderLinkMessage(result.message || 'A code was sent to your existing account.');
        return;
      }
      const provider = providerLinkProviderLabel === 'Microsoft' ? 'Microsoft' : sessionStorage.getItem('simplysend:pendingProviderLinkProvider');
      const providerActions: Record<string, () => Promise<void>> = {
        Amazon: () => loginWithSocialProvider('LoginWithAmazon', 'Amazon'),
        GitLab: () => loginWithSocialProvider('GitLab', 'GitLab'),
        Google: loginWithGoogle,
        LinkedIn: () => loginWithSocialProvider('LinkedIn', 'LinkedIn'),
        Microsoft: () => loginWithMicrosoft(providerLinkDetails.providerName),
        Slack: () => loginWithSocialProvider('Slack', 'Slack'),
      };
      if (provider && providerActions[provider]) {
        setProviderLinkMessage(`Verified. Reopening ${provider} to finish sign-in…`);
        await providerActions[provider]();
      } else {
        setProviderLinkMessage('Verified. Return to sign-in and continue with the same provider to finish connecting it.');
      }
    } catch {
      setProviderLinkMessage('Could not complete verification. Please try again.');
    } finally {
      setProviderLinkBusy(false);
    }
  };

  if (linkChallenge && authView === 'sign-in') {
    return (
      <div className="relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 flex items-center justify-center px-4 py-8 sm:py-12">
        {themeToggle}
        <div className="w-full max-w-md space-y-5 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
          <div>
            <h2 className="text-xl font-semibold"><span>Connect your sign-in method</span></h2>
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400"><span>Review the provider email and the account it will connect to before requesting a verification code. No second account has been created.</span></p>
          </div>
          {providerLinkDetails ? (
            <div className="space-y-2 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs dark:border-zinc-700 dark:bg-zinc-950">
              <p><span className="text-zinc-600 dark:text-zinc-400">{providerLinkProviderLabel} sign-in email</span><br /><span className="break-all text-zinc-900 dark:text-zinc-100">{providerLinkDetails.providerEmail}</span></p>
              <p><span className="text-zinc-600 dark:text-zinc-400">Existing Simply Send account email</span><br /><span className="break-all text-zinc-900 dark:text-zinc-100">{providerLinkDetails.accountEmail}</span></p>
              <p className="text-zinc-600 dark:text-zinc-400"><span>We’ll send the code to {providerLinkDetails.verificationEmail}.</span></p>
            </div>
          ) : <p className="text-xs text-zinc-600 dark:text-zinc-400" role="status"><span>{providerLinkDetailsError || 'Loading account details…'}</span></p>}
          {providerLinkDetails && !providerLinkDetails.matchesRequestedAccount && (
            <p className="text-xs text-amber-800 dark:text-amber-300" role="alert"><span>This is not the account you opened from Account settings. Sign in to the intended provider account and try again. No code has been sent.</span></p>
          )}
          {providerLinkMessage && <p className="text-xs text-zinc-600 dark:text-zinc-400" role="status"><span>{providerLinkMessage}</span></p>}
          <div className="space-y-3">
            <button type="button" disabled={providerLinkBusy || !providerLinkDetails?.matchesRequestedAccount} onClick={() => { void submitProviderLink('request'); }} className="w-full text-xs font-medium text-cyan-700 underline underline-offset-2 hover:text-cyan-800 disabled:opacity-50 dark:text-cyan-300 dark:hover:text-cyan-200"><span>{providerLinkSent ? 'Send a new code' : 'Send verification code'}</span></button>
            <label htmlFor="provider-link-code" className="block text-xs font-medium"><span>Verification code</span></label>
            <input id="provider-link-code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={providerLinkCode} onChange={(event) => setProviderLinkCode(event.target.value.replace(/\D/g, ''))} className="h-10 w-full rounded-md border border-zinc-300 bg-white px-4 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100" />
            <button type="button" disabled={providerLinkBusy || !providerLinkDetails?.matchesRequestedAccount || providerLinkCode.length !== 6} onClick={() => { void submitProviderLink('verify'); }} className="inline-flex h-10 w-full items-center justify-center rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"><span>{providerLinkBusy ? 'Verifying…' : 'Verify and continue'}</span></button>
          </div>
          <button type="button" onClick={() => { sessionStorage.removeItem('simplysend:connectAccountId'); sessionStorage.removeItem('simplysend:pendingProviderLinkProvider'); navigate('/sign-in', { replace: true }); }} className="w-full text-xs text-zinc-600 dark:text-zinc-400 underline underline-offset-2"><span>Back to sign-in</span></button>
        </div>
      </div>
    );
  }

  // Show login form (default)
  return (
    <div className="relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 flex flex-col">
      {themeToggle}
      <MaintenanceBanner />
      <div className="flex justify-center pt-4"><RegionSelector email={email} centerDropdown /></div>
      <div className="flex flex-1 items-center justify-center px-4 py-8 sm:py-12">
        <main className="w-full max-w-md">
          <div className="mb-5 flex items-center justify-center gap-2.5">
            <a href="https://simplysend.email" aria-label="Simply Send home" className="inline-flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
              <span className="relative block h-9 w-9"><img src="/assets/images/sse-logo-bw-256x.png" alt="" className="h-full w-full object-contain dark:hidden" /><img src="/assets/images/sse-logo-bw-256x-dark.png" alt="" className="hidden h-full w-full object-contain dark:block" /></span>
              <span className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">Simply Send</span>
            </a>
          </div>

          <section className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-7">
            <div className="mb-6 text-center">
              <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
                <span>Sign in to your account</span>
              </h1>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                <span>Use your work email or organization sign-in.</span>
              </p>
            </div>

          <form className="space-y-4" onSubmit={handleLogin}>
            {connectingProvider && (
              <div className="space-y-2 rounded-md border border-cyan-200 bg-cyan-50 p-3 dark:border-cyan-800 dark:bg-cyan-950/40">
                <p className="text-xs text-cyan-950 dark:text-cyan-100"><span>Connect {connectingProvider} to your existing account. Use the same sign-in email; we will ask you to confirm the connection through your verified communication email.</span></p>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => {
                    const actions: Record<string, () => Promise<void>> = {
                      Amazon: () => loginWithSocialProvider('LoginWithAmazon', 'Amazon'),
                      GitLab: () => loginWithSocialProvider('GitLab', 'GitLab'),
                      Google: loginWithGoogle,
                      LinkedIn: () => loginWithSocialProvider('LinkedIn', 'LinkedIn'),
                      Microsoft: () => loginWithMicrosoft(),
                      Slack: () => loginWithSocialProvider('Slack', 'Slack'),
                    };
                    const action = actions[connectingProvider];
                    if (action) requestFederatedLogin(action, connectingProvider);
                  }} className="text-xs font-semibold text-cyan-300 underline underline-offset-2"><span>Continue with {connectingProvider}</span></button>
                  <button type="button" onClick={() => { sessionStorage.removeItem('simplysend:connectProvider'); sessionStorage.removeItem('simplysend:connectAccountId'); setConnectingProvider(null); }} className="text-xs text-zinc-600 underline underline-offset-2 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"><span>Cancel</span></button>
                </div>
              </div>
            )}
            {formNotice && (
              <div className="rounded-md border border-cyan-200 bg-cyan-50 p-3 dark:border-cyan-800 dark:bg-cyan-950/40">
                <p className="text-sm text-cyan-950 dark:text-cyan-100"><span>{formNotice}</span></p>
              </div>
            )}
            {error && error === 'MAINTENANCE_MODE' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-600 mb-1">
                      System Maintenance in Progress
                    </h3>
                    <p className="text-xs text-blue-700">
                      We are currently updating our system infrastructure. Account access or email sending may be affected.
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      Check details on <a href="https://simplesend.email" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 dark:hover:text-blue-800 hover:underline">website home page</a>.
                    </p>
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/40">
                <p className="text-sm text-red-700 dark:text-red-200"><span>{error}</span></p>
                {loginStep === 'password' && error === 'Incorrect username or password.' && (
                  <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
                    <span>Have you created an account? If not, </span>
                    <button
                      type="button"
                      onClick={() => {
                        clearLoginErrors();
                        goToAuthView('sign-up');
                      }}
                      className="font-medium text-cyan-700 hover:text-cyan-800 underline underline-offset-2 transition-colors dark:text-cyan-300 dark:hover:text-cyan-200"
                    >
                      <span>sign up</span>
                    </button>
                    <span> to get started.</span>
                  </p>
                )}
              </div>
            ) : null}

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1.5 flex justify-between text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  <span>Work email</span>
                  {loginStep !== 'email' && (
                    <button
                      type="button"
                      onClick={goBackToEmailStep}
                      className="text-xs text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200"
                    >
                      <span>Edit</span>
                    </button>
                  )}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-zinc-400 dark:text-zinc-500" />
                  </div>
                  <input
                    ref={emailInputRef}
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    readOnly={loginStep !== 'email'}
                    className={`h-10 w-full rounded-md border border-zinc-300 bg-white pl-10 pr-4 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:border-zinc-700 dark:text-zinc-100 dark:placeholder-zinc-500 ${loginStep !== 'email' ? 'cursor-not-allowed opacity-60 dark:bg-zinc-800' : 'dark:bg-zinc-950'}`}
                    placeholder="yours@company.com"
                    value={email}
                    onChange={(e) => {
                      // Email is read-only after Continue. Ignore autofill/spurious
                      // change events so they cannot bounce the flow back to email.
                      if (loginStep !== 'email') {
                        return;
                      }
                      setEmail(e.target.value);
                    }}
                  />
                </div>
              </div>

              {loginStep === 'passkey' && (
                <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-center dark:border-zinc-800 dark:bg-zinc-950">
                  <Fingerprint className="mx-auto mb-2 h-8 w-8 text-cyan-700 dark:text-cyan-300" />
                  <p className="text-sm text-zinc-900 dark:text-zinc-100">
                    <span>{passkeyLoading ? 'Opening passkey sign-in…' : 'Use your passkey to continue'}</span>
                  </p>
                  {passkeyRequired ? (
                    <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                      <span>Password sign-in is disabled for this account.</span>
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                      <span>Your browser will prompt for Face ID, Touch ID, or Windows Hello.</span>
                    </p>
                  )}
                </div>
              )}

              {loginStep === 'password' && (
                <div className="relative animate-in slide-in-from-top-4 fade-in duration-300">
                  <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Password
                  </label>
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none mt-6">
                    <Lock className="h-4 w-4 text-zinc-400 dark:text-zinc-500" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    autoFocus
                    className="h-10 w-full rounded-md border border-zinc-300 bg-white pl-10 pr-10 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-500"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center mt-6"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                    )}
                  </button>
                </div>
              )}
            </div>

            <div className="pt-1">
              {/* Email step: Continue button */}
              {loginStep === 'email' && (
                <button
                  type="submit"
                  disabled={loading || ssoCheckLoading || !email.trim()}
                  className="h-10 w-full rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {ssoCheckLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Checking…</span>
                    </div>
                  ) : (
                    <span>Continue with Password/SSO</span>
                  )}
                </button>
              )}
              {/* SSO detected: adaptive IdP button */}
              {loginStep === 'sso-detected' && ssoResult && ssoRouteType && (
                <div className="w-full space-y-1">
                  {/* Detected badge */}
                  <div className="flex items-center gap-2 px-3 py-2 text-xs justify-center">
                    <Lock className="w-3 h-3 shrink-0 text-cyan-700 dark:text-cyan-300" />
                    <span>
                      Use your{' '}
                      {ssoResult.u ? (
                        <a
                          href={ssoResult.u}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-cyan-700 underline underline-offset-2 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200"
                        >
                          {ssoRouteLabel}
                        </a>
                      ) : (
                        <span className="font-semibold">{ssoRouteLabel}</span>
                      )}
                      {' '}account
                    </span>
                  </div>

                  {/* Branded button */}
                  <button
                    id="sso-idp-btn"
                    type="button"
                    onClick={() => requestFederatedLogin(async () => {
                      if (ssoRouteType === 'saml') {
                        await loginWithSSO(ssoResult.p);
                      } else if (ssoRouteType === 'google' && (!ssoResult.p || ssoResult.p === 'Google')) {
                        await loginWithGoogle();
                      } else {
                        await loginWithMicrosoft(ssoResult.p);
                      }
                    }, ssoRouteLabel)}
                    disabled={loading || federatedLoginProvider !== null}
                    className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading || federatedLoginProvider === ssoRouteLabel ? (
                      <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${ssoRouteType === 'saml' ? 'border-zinc-800' : 'border-white'}`} />
                    ) : ssoRouteType === 'google' ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                    ) : ssoRouteType === 'azure' ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" className="h-4 w-4 shrink-0" aria-hidden="true">
                        <rect x="0" y="0" width="10" height="10" fill="#f25022" />
                        <rect x="11" y="0" width="10" height="10" fill="#7fba00" />
                        <rect x="0" y="11" width="10" height="10" fill="#00a4ef" />
                        <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
                      </svg>
                    ) : (
                      <Building className="h-4 w-4 shrink-0 text-white" />
                    )}
                    <span>{federatedLoginProvider === ssoRouteLabel ? `Opening ${ssoRouteLabel}…` : `Continue with ${ssoRouteLabel}`}</span>
                  </button>

                  {/* Back link */}
                  <button
                    type="button"
                    onClick={goBackToEmailStep}
                    className="w-full text-center text-sm font-medium text-cyan-700 underline underline-offset-2 transition-colors hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200"
                  >
                    ← Use a different email
                  </button>
                </div>
              )}

              {/* Passkey step */}
              {loginStep === 'passkey' && (
                <div className="w-full space-y-3">
                  <button
                    type="button"
                    onClick={handlePasskeySignIn}
                    disabled={loading || passkeyLoading || !email.trim()}
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {passkeyLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <>
                        <Fingerprint className="h-4 w-4" />
                        <span>Sign in with passkey</span>
                      </>
                    )}
                  </button>
                  {!passkeyRequired && (
                    <button
                      type="button"
                      onClick={() => setLoginStep('password')}
                      disabled={passkeyLoading}
                      className="w-full text-center text-sm font-medium text-cyan-700 underline underline-offset-2 transition-colors hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200"
                    >
                      <span>Use password instead</span>
                    </button>
                  )}
                </div>
              )}

              {/* Password step: Sign In */}
              {loginStep === 'password' && (
                <div className="w-full space-y-3">
                  <button
                    type="submit"
                    disabled={loading || passkeyLoading || !email.trim()}
                    className="h-10 w-full rounded-md bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading && !passkeyLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      </div>
                    ) : (
                      <span>Sign In</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handlePasskeySignIn}
                    disabled={loading || passkeyLoading || !email.trim() || !passkeysSupported}
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-zinc-300 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-500 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  >
                    {passkeyLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <>
                        <Fingerprint className="h-4 w-4" />
                        <span>Sign in with passkey</span>
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-zinc-600 dark:text-zinc-400">
                    <span>If password sign-in is unavailable, use your passkey. If an owner started recovery, open Forgot password and choose the owner reset code option.</span>
                  </p>
                </div>
              )}
            </div>

            {/* Configured production providers. */}
            {loginStep === 'email' && (
              <div className="mt-5">
                <div className="mb-3 flex items-center gap-3" aria-hidden="true">
                  <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                  <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">or</span>
                  <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                </div>
                <p className="mb-3 text-center text-xs text-zinc-600 dark:text-zinc-400">
                  <span>Use the provider account associated with your work email.</span>
                </p>
                <div className="space-y-2">
                  <button type="button" className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-md border border-zinc-300 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-500 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800" onClick={() => requestFederatedLogin(() => loginWithSocialProvider('LoginWithAmazon', 'Amazon'), 'Amazon')} disabled={loading || federatedLoginProvider !== null}>
                    {federatedLoginProvider === 'Amazon' ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-2 border-zinc-500 border-t-cyan-400" /><span>Opening Amazon…</span></>
                    ) : (
                      <><img src="/assets/auth/aboutamazon-com-logo.png" alt="" className="h-5 w-5 shrink-0 rounded-sm object-cover" /><span>Continue with Amazon</span></>
                    )}
                  </button>
                  <button type="button" className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-md border border-zinc-300 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-500 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800" onClick={() => requestFederatedLogin(() => loginWithSocialProvider('GitLab', 'GitLab'), 'GitLab')} disabled={loading || federatedLoginProvider !== null}>
                    {federatedLoginProvider === 'GitLab' ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-2 border-zinc-500 border-t-cyan-400" /><span>Opening GitLab…</span></>
                    ) : (
                      <><svg className="h-5 w-5 shrink-0 fill-[#FC6D26]" viewBox="0 0 24 24" aria-hidden="true"><path d="m12 21-3.1-3.3L3.4 12l2.2-7.1c.2-.6 1-.6 1.2 0L9 12h6l2.2-7.1c.2-.6 1-.6 1.2 0l2.2 7.1-5.5 5.7L12 21Z" /></svg><span>Continue with GitLab</span></>
                    )}
                  </button>
                  <button type="button" className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-md border border-zinc-300 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-500 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800" onClick={() => requestFederatedLogin(loginWithGoogle, 'Google')} disabled={loading || federatedLoginProvider !== null}>
                    {federatedLoginProvider === 'Google' ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-2 border-zinc-500 border-t-cyan-400" /><span>Opening Google…</span></>
                    ) : (
                      <><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg><span>Continue with Google</span></>
                    )}
                  </button>
                  <button type="button" className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-md border border-zinc-300 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-500 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800" onClick={() => requestFederatedLogin(() => loginWithSocialProvider('LinkedIn', 'LinkedIn'), 'LinkedIn')} disabled={loading || federatedLoginProvider !== null}>
                    {federatedLoginProvider === 'LinkedIn' ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-2 border-zinc-500 border-t-cyan-400" /><span>Opening LinkedIn…</span></>
                    ) : (
                      <><span className="h-5 w-5 shrink-0 overflow-hidden rounded-[3px]" aria-hidden="true"><img src="/assets/auth/linkedin-logo-short.png" alt="" className="h-5 w-auto max-w-none" /></span><span>Continue with LinkedIn</span></>
                    )}
                  </button>
                  <button type="button" className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-md border border-zinc-300 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-500 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800" onClick={() => requestFederatedLogin(() => loginWithMicrosoft(), 'Microsoft')} disabled={loading || federatedLoginProvider !== null}>
                    {federatedLoginProvider === 'Microsoft' ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-2 border-zinc-500 border-t-cyan-400" /><span>Opening Microsoft…</span></>
                    ) : (
                      <><img src="/assets/auth/microsoft-logo-short.svg" alt="" className="h-5 w-5 shrink-0 object-contain" /><span>Continue with Microsoft</span></>
                    )}
                  </button>
                  <button type="button" className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-md border border-zinc-300 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-500 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800" onClick={() => requestFederatedLogin(() => loginWithSocialProvider('Slack', 'Slack'), 'Slack')} disabled={loading || federatedLoginProvider !== null}>
                    {federatedLoginProvider === 'Slack' ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-2 border-zinc-500 border-t-cyan-400" /><span>Opening Slack…</span></>
                    ) : (
                      <><img src="/assets/auth/slack-logo-short.jpg" alt="" className="h-5 w-5 shrink-0 rounded-sm object-contain" /><span>Continue with Slack</span></>
                    )}
                  </button>
                </div>
              </div>
            )}

            {loginStep === 'email' && (
              <div className="flex flex-col items-center gap-1.5 border-t border-zinc-200 pt-4 text-center dark:border-zinc-800">
                <button
                  type="button"
                  onClick={() => {
                    clearLoginErrors();
                    goToAuthView('sign-up');
                  }}
                  className="text-sm font-medium text-cyan-700 hover:text-cyan-800 underline underline-offset-2 transition-colors dark:text-cyan-300 dark:hover:text-cyan-200"
                >
                  Don&apos;t have an account?
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearLoginErrors();
                    setForgotPasswordEmail(email);
                    goToAuthView('forgot-password');
                  }}
                  className="text-sm font-medium text-cyan-700 hover:text-cyan-800 underline underline-offset-2 transition-colors dark:text-cyan-300 dark:hover:text-cyan-200"
                >
                  Forgot password?
                </button>
                <a
                  href="https://simplysend.email/docs/guide/enterprise-sso"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-cyan-700 hover:text-cyan-800 underline underline-offset-2 transition-colors dark:text-cyan-300 dark:hover:text-cyan-200"
                >
                  Onboard your SSO/Enterprise Account?
                </a>
              </div>
            )}
            {loginStep === 'password' && (
              <div className="flex flex-col items-center gap-3 pt-2 text-center">
                <button
                  type="button"
                  onClick={() => {
                    clearLoginErrors();
                    goToAuthView('sign-up');
                  }}
                  className="text-sm font-medium text-cyan-700 hover:text-cyan-800 underline underline-offset-2 transition-colors dark:text-cyan-300 dark:hover:text-cyan-200"
                >
                  <span>Don&apos;t have an account?</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearLoginErrors();
                    setForgotPasswordEmail(email);
                    goToAuthView('forgot-password');
                  }}
                  className="text-sm font-medium text-cyan-700 underline underline-offset-2 transition-colors hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200"
                >
                  Forgot password?
                </button>
                <p className="text-sm text-zinc-700 dark:text-zinc-300">
                  SSO not working?{' '}
                  <a
                    href="https://simplysend.email/docs/guide/troubleshooting/sso-login-issues"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-cyan-700 underline underline-offset-2 transition-colors hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200"
                  >
                    Troubleshoot →
                  </a>
                </p>
              </div>
            )}
          </form>


          </section>

          <div className="mx-auto max-w-sm px-3 pt-5 text-center">
            <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
              By continuing, you acknowledge that your use of Simply Send is subject to our{' '}
              <a
                href="https://simplysend.email/legal/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-700 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-300 dark:hover:text-cyan-300"
              >
                Terms of Service
              </a>
              ,{' '}
              <a href="https://simplysend.email/legal/dpa" target="_blank" rel="noopener noreferrer" className="text-zinc-700 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-300 dark:hover:text-cyan-300">Data Processing Addendum</a>
              ,{' '}
              <a
                href="https://simplysend.email/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-700 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-300 dark:hover:text-cyan-300"
              >
                Privacy Policy
              </a>
              , and{' '}
              <a
                href="https://simplysend.email/legal/acceptable-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-700 underline underline-offset-2 hover:text-cyan-700 dark:text-zinc-300 dark:hover:text-cyan-300"
              >
                Acceptable Use Policy
              </a>
              .
            </p>
          </div>
        </main>
      </div>
      {showAupModal && (
        <AupAcknowledgementModal
              release={signupRelease}
          title="Before you continue"
          confirmLabel="I Agree — Continue"
          checks={aupChecks}
          allChecked={allAupChecked}
          loading={loading}
          onChecksChange={setAupChecks}
          onCancel={dismissAupModal}
          onConfirm={() => { void handleAupConfirmation(); }}
        />
      )}
    </div>
  );
}
